import React, { useEffect, useMemo } from 'react';
import { Box, Collapse, Divider, Stack, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { UseFormReturn } from 'react-hook-form';

import { FormQuestion, FormQuestionType } from '../../../../../../../Utils/Types/FormBuilder';
import withOptional from '../../../../../../../Utils/Posts/Form/withOptional';
import AttachmentsShow from './AttachmentsShow';
import CheckboxShow from './CheckboxShow';
import DateShow from './DateShow';
import TextShow from './TextShow';
import TextareaShow from './TextareaShow';
import RadioShow from './RadioShow';
import PrivateFieldsWrapper from '../../../../../../DesignSystem/PrivateFieldsWrapper';
import ConditionalWrapper from '../../../../../../ConditionalWrapper';
import LocationShow from './LocationShow';
import PictureShow from './PictureShow';
import BudgetShow from './BudgetShow';
import PhoneNumberShow from './PhoneNumberShow';
import { LocaleType } from '../../../../../../../Utils/Types/Locales';
import ReadMore from '../../../../../../ReadMore/ReadMore';
import useQuestionVisibility from '../../../../../../../Hooks/useQuestionVisibility';

export type QuestionTypeShowProps = {
  disabled?: boolean;
  errors: any;
  form: UseFormReturn<any>;
  index: number;
  lang: LocaleType;
  errorIndex?: number;
  question: FormQuestion;
  scope?: string;
  position?: number;
  setDisabledForm?: any;
  setError?: any;
  size?: 'small' | undefined;
  hiddenLabel?: boolean;
  geojson?: any;
};

const components: Record<FormQuestionType, React.FC<QuestionTypeShowProps>> = {
  attachment: AttachmentsShow,
  checkbox: CheckboxShow,
  date: DateShow,
  location: LocationShow,
  money: BudgetShow,
  phone_number: PhoneNumberShow,
  picture: PictureShow,
  radio: RadioShow,
  text: TextShow,
  textarea: TextareaShow,
  section: TextareaShow,
};

interface QuestionShowProps {
  position?: number;
  disabled?: boolean;
  errors: any;
  errorIndex?: number;
  form: UseFormReturn<any>;
  geojson?: string;
  hiddenLabel?: boolean;
  index: number;
  lang: LocaleType;
  question: FormQuestion;
  questions?: FormQuestion[];
  scope?: string;
  setDisabledForm?: any;
  setError?: any;
  divider?: boolean;
}

export default function QuestionShow({
  disabled = false,
  errors,
  form,
  geojson,
  hiddenLabel = false,
  index,
  errorIndex,
  lang,
  question,
  questions = [],
  scope = '',
  setDisabledForm,
  setError,
  divider = false,
}: QuestionShowProps) {
  const visible = useQuestionVisibility({
    question,
    questions,
    scope,
    form,
  });

  const Component = components[question.field_type];

  useEffect(() => {
    // NOTE: We don't want to send hidden fields to the server
    // so we set the hidden field to true when the question is not visible
    // and we filter them on generateDate() in the handleSubmit function
    form.setValue(`${scope}answers_attributes.${index}.hidden`, !visible);
  }, [visible]);

  return useMemo(
    () => (
      <Collapse in={visible} mountOnEnter unmountOnExit>
        <Stack spacing={3}>
          <ConditionalWrapper
            condition={question?.confidential}
            wrapper={(children: any) => (
              <PrivateFieldsWrapper
                alertText={
                  <FormattedMessage id="POST_FORM.FORM_BUILDER.CONFIDENTIAL_INFORMATION_NOTICE" />
                }
              >
                {[children]}
              </PrivateFieldsWrapper>
            )}
          >
            <Box>
              <input
                type="hidden"
                {...form.control.register(`${scope}answers_attributes.${index}.id`)}
              />
              <input
                // adds question foreign key to answers_attributes
                type="hidden"
                {...form.control.register(`${scope}answers_attributes.${index}.question_id`)}
                value={question.id}
              />

              <Box component="header" sx={{ mb: 2 }}>
                <label>
                  {!hiddenLabel && (
                    <Typography variant="h6" component="p">
                      {question.number_label ? question.number_label + '. ' : ''}
                      {withOptional(question?.title, question.required, true)}
                    </Typography>
                  )}
                  <ReadMore content={question?.description || ''} />
                </label>
              </Box>
              <Component
                disabled={disabled}
                errorIndex={errorIndex}
                errors={errors}
                form={form}
                geojson={geojson}
                hiddenLabel
                index={index}
                lang={lang}
                question={question}
                scope={scope}
                setDisabledForm={setDisabledForm}
                setError={setError}
              />
            </Box>
          </ConditionalWrapper>

          {divider && <Divider />}
        </Stack>
      </Collapse>
    ),
    [visible, Component, question, errors, lang, setError, setDisabledForm, index, disabled, scope],
  );
}
